/**
 * Authentication Context - SolarPredict
 * 
 * This file implements a complete authentication system using React's Context API.
 * It manages the user lifecycle (login, registration, logout) and maintains
 * authentication state throughout the application.
 * 
 * The system uses JWT (JSON Web Token) tokens stored in localStorage to
 * allow session persistence, even after page reload.
 * 
 * The authentication API uses the standardized endpoint on port 4000
 * (after migration from port 5000).
 *
 * Developed by: [Your Name]
 * Date: July 2025
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginUser, registerUser, logoutUser, getCurrentUser } from '../api/auth';

/**
 * Authentication context creation
 * 
 * The context is initialized to null and will be filled by the AuthProvider
 * with user information and authentication functions.
 */
const AuthContext = createContext(null);

/**
 * Custom hook to use the authentication context
 * 
 * This hook allows any component to easily access authentication state
 * and associated functions without having to pass through props.
 * 
 * Usage example:
 * const { currentUser, login, logout, isAuthenticated } = useAuth();
 * 
 * @returns {Object} The authentication context with all its values and functions
 */
export const useAuth = () => {
  return useContext(AuthContext);
};

/**
 * Fournisseur du contexte d'authentification
 * 
 * Ce composant encapsule l'ensemble de la logique d'authentification et la rend
 * disponible à tous les composants enfants via le Context API.
 * 
 * Il gère:
 * - L'état de l'utilisateur connecté
 * - Le chargement des données d'authentification
 * - Les erreurs d'authentification
 * - Les fonctions de connexion, inscription et déconnexion
 * 
 * @param {Object} props - Les propriétés du composant
 * @param {React.ReactNode} props.children - Les composants enfants qui auront accès au contexte
 */
export const AuthProvider = ({ children }) => {
  // États du contexte d'authentification
  const [currentUser, setCurrentUser] = useState(null);  // Informations sur l'utilisateur connecté
  const [loading, setLoading] = useState(true);         // Indicateur de chargement
  const [error, setError] = useState(null);             // Message d'erreur éventuel

  /**
   * Vérification automatique de l'authentification au chargement de l'application
   * 
   * Ce hook useEffect s'exécute une seule fois au montage du composant.
   * Il vérifie si un token JWT existe dans le localStorage (via l'intercepteur Axios)
   * et tente de récupérer les informations de l'utilisateur depuis le backend.
   * 
   * Si la requête réussit, l'utilisateur est automatiquement reconnecté.
   * En cas d'échec, l'utilisateur reste déconnecté (token invalide ou expiré).
   */
  useEffect(() => {
    const checkUserLoggedIn = async () => {
      try {
        setLoading(true); // Activer l'indicateur de chargement
        
        // Vérifier si un token existe d'abord
        const token = localStorage.getItem('token');
        console.log('Auth check - Token found:', !!token);
        
        if (!token) {
          console.log('Auth check - No token found, user not authenticated');
          setLoading(false);
          return;
        }
        
        // Appel API pour vérifier si le token JWT est valide
        // L'intercepteur Axios dans auth.js ajoute automatiquement le token depuis localStorage
        console.log('Auth check - Verifying token with backend...');
        const response = await getCurrentUser();
        
        if (response.success) {
          // Si la réponse est positive, mettre à jour l'état avec les données utilisateur
          console.log('Auth check - User authenticated successfully:', response.user.email);
          setCurrentUser(response.user);
        } else {
          console.log('Auth check - Backend returned unsuccessful response');
        }
        // Si la réponse est négative, l'utilisateur reste déconnecté (currentUser = null)
      } catch (error) {
        // Gestion des erreurs (problème réseau, serveur indisponible, etc.)
        console.error('Auth check - Failed to fetch current user:', error);
        // L'utilisateur reste déconnecté en cas d'erreur
      } finally {
        // Quoi qu'il arrive, désactiver l'indicateur de chargement
        console.log('Auth check - Completed');
        setLoading(false);
      }
    };

    // Exécuter la vérification d'authentification
    checkUserLoggedIn();
  }, []); // Dépendance vide = exécution uniquement au montage du composant

  /**
   * Fonction de connexion utilisateur
   * 
   * Cette fonction authentifie un utilisateur avec son email et son mot de passe.
   * En cas de succès, elle:
   * 1. Met à jour l'état avec les informations de l'utilisateur
   * 2. Stocke le token JWT dans le localStorage
   * 3. Retourne un objet de réponse avec succès = true
   *
   * @param {string} email - Adresse email de l'utilisateur
   * @param {string} password - Mot de passe de l'utilisateur
   * @returns {Object} Objet indiquant le succès ou l'échec de la connexion
   */
  const login = async (email, password) => {
    try {
      // Sous-fonction pour gérer la connexion et stocker le token
      const handleLogin = async (email, password) => {
        try {
          // Appel au service API d'authentification
          const response = await loginUser(email, password);
          
          if (response.success) {
            // Stocker les informations utilisateur dans le state
            setCurrentUser(response.user);
            
            // Stocker le token JWT dans localStorage pour les futures requêtes
            // Ce token sera utilisé par l'intercepteur Axios dans auth.js
            localStorage.setItem('token', response.token);
            return response;
          }
          return response;
        } catch (error) {
          console.error('Error during login:', error);
          return { success: false, message: 'Error during login.' };
        }
      };
      
      // Mise à jour des états avant la tentative de connexion
      setLoading(true); // Activer l'indicateur de chargement
      setError(null);   // Réinitialiser les erreurs précédentes
      
      // Tentative de connexion
      const response = await handleLogin(email, password);
      
      if (response.success) {
        // Connexion réussie
        return { success: true };
      } else {
        // Login failure with server error message
        setError(response.message || 'Login failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      // Handle unexpected errors (network issues, server, etc.)
      setError(error.response?.data?.message || 'Server error');
      return { success: false, message: error.response?.data?.message || 'Server error' };
    } finally {
      // Quoi qu'il arrive, désactiver l'indicateur de chargement
      setLoading(false);
    }
  };

  /**
   * Fonction d'inscription d'un nouvel utilisateur
   * 
   * Cette fonction crée un nouveau compte utilisateur avec un email et un mot de passe.
   * En cas de succès, elle connecte automatiquement l'utilisateur.
   * 
   * Note: Le token JWT est géré par la fonction loginUser qui est appelée par
   * le backend après une inscription réussie.
   *
   * @param {string} email - Adresse email du nouvel utilisateur
   * @param {string} password - Mot de passe choisi
   * @returns {Object} Objet indiquant le succès ou l'échec de l'inscription
   */
  const register = async (email, password) => {
    try {
      // Mise à jour des états avant la tentative d'inscription
      setLoading(true); // Activer l'indicateur de chargement
      setError(null);   // Réinitialiser les erreurs précédentes
      
      // Appel au service API d'inscription
      const response = await registerUser(email, password);
      
      if (response.success) {
        // Inscription réussie - connecter automatiquement l'utilisateur
        setCurrentUser(response.user);
        // Le token JWT est déjà stocké par le service d'API
        return { success: true };
      } else {
        // Registration failure with server error message
        setError(response.message || 'Registration failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      // Handle unexpected errors (network issues, server, etc.)
      setError(error.response?.data?.message || 'Server error');
      return { success: false, message: error.response?.data?.message || 'Server error' };
    } finally {
      // Quoi qu'il arrive, désactiver l'indicateur de chargement
      setLoading(false);
    }
  };

  /**
   * Fonction de déconnexion utilisateur
   * 
   * Cette fonction déconnecte l'utilisateur en:
   * 1. Supprimant le token JWT du localStorage (via le service API)
   * 2. Supprimant les informations utilisateur du state
   * 3. Réinitialisant le contexte d'authentification
   * 
   * Après déconnexion, l'utilisateur est redirigé vers la page de connexion
   * par les routes protégées.
   *
   * @returns {Object} Objet indiquant le succès ou l'échec de la déconnexion
   */
  const logout = async () => {
    try {
      // Mise à jour des états avant la tentative de déconnexion
      setLoading(true); // Activer l'indicateur de chargement
      setError(null);   // Réinitialiser les erreurs précédentes
      
      // Appel au service API de déconnexion
      // Cette fonction supprime également le token du localStorage
      await logoutUser();
      
      // Suppression des informations utilisateur du state
      setCurrentUser(null);
      
      // Clear all localStorage data (prediction state, chart cache, etc.)
      console.log('🧹 Clearing all localStorage data on logout...');
      localStorage.removeItem('gepredict_persistent_state');  // Prediction state
      localStorage.removeItem('gepredict_chart_data_cache');  // Chart cache
      localStorage.removeItem('token');  // Auth token (already done in logoutUser but ensure)
      
      return { success: true };
    } catch (error) {
      // Handle unexpected errors (network issues, server, etc.)
      setError(error.response?.data?.message || 'Logout error');
      return { success: false, message: error.response?.data?.message || 'Logout error' };
    } finally {
      // Quoi qu'il arrive, désactiver l'indicateur de chargement
      setLoading(false);
    }
  };

  /**
   * Valeurs et fonctions exposées par le contexte d'authentification
   * 
   * Ces valeurs seront accessibles depuis n'importe quel composant via le hook useAuth().
   * J'ai inclus un flag pratique isAuthenticated qui permet de vérifier rapidement
   * si un utilisateur est connecté ou non, sans avoir à vérifier currentUser != null.
   */
  const value = {
    currentUser,                   // Objet contenant les informations de l'utilisateur
    loading,                       // Booléen indiquant si une opération est en cours
    error,                         // Message d'erreur éventuel
    login,                         // Fonction de connexion
    register,                      // Fonction d'inscription
    logout,                        // Fonction de déconnexion
    isAuthenticated: !!currentUser, // Booléen indiquant si un utilisateur est connecté
    token: localStorage.getItem('token') // Le token JWT actuel (utile pour les tests)
  };

  /**
   * Rendu du Provider qui encapsule tous les composants de l'application
   * 
   * Ce Provider rend disponible l'ensemble des valeurs et fonctions d'authentification
   * à tous les composants enfants, permettant ainsi une gestion centralisée
   * de l'authentification dans toute l'application.
   */
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
